import { cn } from "@tellescope/ui";
import { EditorContent } from "@tiptap/react";
import { DragEvent, memo, useRef, useState } from "react";

import { TEMPLATE_TOKEN_NODE_NAME } from "../editor/TemplateTokenNode";
import { TokenPalette, TokenPaletteHandle } from "../palette/TokenPalette";
import { TOKEN_DND_MIME, TOKEN_ID_DND_MIME } from "../palette/tokenDrag";
import { EditorToolbar } from "./EditorToolbar";
import { HtmlModeView } from "./HtmlModeView";
import { HtmlTokenField, HtmlTokenFieldHandle } from "./HtmlTokenField";
import { ModeTabs } from "./ModeTabs";
import type { TemplateEditorMode, TemplateEditorProps, TemplateEditorTab } from "./types";
import { useTemplateEditor } from "./useTemplateEditor";

const tabToMode = (tab: TemplateEditorTab): TemplateEditorMode => (tab === "html" ? "html" : "richtext");
const modeToInitialTab = (mode: TemplateEditorMode): TemplateEditorTab => (mode === "html" ? "html" : "text");

/**
 * Template body editor: a "Text" surface (rich or plaintext, per `richTextEnabled`) and an "HTML"
 * surface, with a collapsible, searchable token palette. Tokens render as chips, drag or click into
 * the body, and serialize back to the stored `message`/`html`. See `.plans` history and the
 * templated-strings reference for the contract.
 *
 * Memoized: the editor mounts a TipTap instance whose re-renders are costly, so it skips renders
 * when its props are unchanged. Consumers should pass a stable `onChange` and a memoized `value`
 * (see `useTemplateFormDialog`) so unrelated form-field keystrokes don't re-render it.
 */
const TemplateEditorInner = ({
  value,
  onChange,
  disabled,
  className,
  preserveAnchorTags,
  id,
  richTextEnabled = true,
}: TemplateEditorProps) => {
  const { editor, setHtml, setMessage, loadHtmlIntoEditor, insertToken } = useTemplateEditor({
    value,
    onChange,
    disabled,
    className,
    preserveAnchorTags,
  });
  const [activeTab, setActiveTab] = useState<TemplateEditorTab>(() => modeToInitialTab(value.mode));
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const htmlFieldRef = useRef<HtmlTokenFieldHandle>(null);
  const paletteSearchRef = useRef<HTMLInputElement>(null);
  const paletteHandleRef = useRef<TokenPaletteHandle>(null);

  // The Text tab is a plaintext surface (editing `message`) when rich formatting is disabled; both
  // the basic text field and the HTML field are contentEditable HtmlTokenFields (shared htmlFieldRef).
  const isBasicText = activeTab === "text" && !richTextEnabled;
  const usesTokenField = activeTab === "html" || isBasicText;

  const togglePalette = () => {
    const willOpen = !paletteOpen;
    setPaletteOpen(willOpen);
    // The palette is always mounted (for the open/close transition), so autofocus-on-mount can't
    // work; focus the search here when opening instead.
    if (willOpen) requestAnimationFrame(() => paletteSearchRef.current?.focus());
  };

  // Token insertion targets the active surface: the token field's caret (HTML or basic text), or the
  // TipTap document (rich text).
  const handleInsertToken = (raw: string) => {
    if (usesTokenField) htmlFieldRef.current?.insertToken(raw);
    else insertToken(raw);
  };

  // In the basic text surface, emoji inserts as plain text into the token field; in the rich surface
  // it goes into the TipTap document (the toolbar's default when this is left undefined).
  const handleInsertEmoji = isBasicText ? (emoji: string) => htmlFieldRef.current?.insertText(emoji) : undefined;

  const handleTabChange = (tab: TemplateEditorTab) => {
    setActiveTab(tab);
    const mode = tabToMode(tab);
    if (mode !== value.mode) onChange({ ...value, mode });
    // Reseed the TipTap document from stored HTML only when entering the rich text surface; the basic
    // text and HTML surfaces are contentEditable fields that seed themselves on mount.
    if (tab === "text" && richTextEnabled) loadHtmlIntoEditor();
  };

  const insertTokenAt = (raw: string, pos: number) => {
    editor?.chain().focus().insertContentAt(pos, { type: TEMPLATE_TOKEN_NODE_NAME, attrs: { raw } }).run();
  };

  // A param-less drop carries its built raw and inserts immediately at the drop position; a
  // parameterized drop carries a token id and opens the collector at the drop point, deferring the
  // insert to the captured position until its params are submitted.
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!editor) return;
    const raw = event.dataTransfer.getData(TOKEN_DND_MIME);
    const tokenId = event.dataTransfer.getData(TOKEN_ID_DND_MIME);
    if (!raw && !tokenId) return;
    event.preventDefault();
    const coords = editor.view.posAtCoords({ left: event.clientX, top: event.clientY });
    const pos = coords?.pos ?? editor.state.selection.from;
    if (raw) {
      insertTokenAt(raw, pos);
      return;
    }
    paletteHandleRef.current?.beginDropCollection({
      tokenId,
      anchor: { x: event.clientX, y: event.clientY },
      insertAt: (built) => insertTokenAt(built, pos),
    });
  };

  return (
    // A definite height (capped) gives the body row a bound so the palette can scroll internally.
    <div className={cn("flex h-[60svh] max-h-[520px] min-h-0 flex-col gap-2", className)}>
      {/* Mode tabs sit above the bordered editor, not inside it. */}
      <ModeTabs activeTab={activeTab} onTabChange={handleTabChange} />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded border">
        {/* Full-width toolbar so it doesn't reflow when the palette opens/closes. */}
        <EditorToolbar
          editor={editor}
          showFormatting={activeTab === "text" && richTextEnabled}
          showEmoji={activeTab !== "html"}
          htmlMode={activeTab === "html"}
          showPreview={showHtmlPreview}
          onShowPreview={setShowHtmlPreview}
          paletteOpen={paletteOpen}
          onTogglePalette={togglePalette}
          onInsertEmoji={handleInsertEmoji}
        />

        <div className="flex min-h-0 flex-1">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {activeTab === "html" ? (
              <HtmlModeView
                id={id}
                html={value.html}
                onChange={setHtml}
                fieldRef={htmlFieldRef}
                showPreview={showHtmlPreview}
                disabled={disabled}
                preserveAnchorTags={preserveAnchorTags}
              />
            ) : isBasicText ? (
              // Plaintext surface editing `message` directly (no formatting, no images). Reuses the
              // token-aware contentEditable field, whose source string is the plaintext `message`.
              <div className="flex min-h-0 flex-1 flex-col">
                <HtmlTokenField
                  id={id}
                  ref={htmlFieldRef}
                  html={value.message}
                  onChange={setMessage}
                  disabled={disabled}
                  preserveAnchorTags={preserveAnchorTags}
                  className="font-sans text-base"
                />
              </div>
            ) : (
              <div
                className="min-h-0 flex-1 cursor-text overflow-y-auto p-2"
                onDrop={handleDrop}
                onDragOver={(event) => event.preventDefault()}
                onMouseDown={(event) => {
                  // Clicking the empty area below the text focuses the editor at the end.
                  if (event.target === event.currentTarget) {
                    event.preventDefault();
                    editor?.chain().focus("end").run();
                  }
                }}
              >
                <EditorContent id={id} editor={editor} className="h-full" />
              </div>
            )}
          </div>
          {/* Always mounted so the open/close width transition can ease in both directions. */}
          <div
            className={cn(
              "flex min-h-0 shrink-0 flex-col overflow-hidden border-l transition-[width] duration-200 ease-in-out",
              paletteOpen ? "w-64" : "w-0 border-l-0"
            )}
          >
            {/* flex-1 so it fills the bounded panel height, giving the palette a scroll bound. */}
            <div className="flex min-h-0 w-64 flex-1 flex-col p-2">
              <TokenPalette onInsert={handleInsertToken} searchRef={paletteSearchRef} handleRef={paletteHandleRef} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TemplateEditor = memo(TemplateEditorInner);
